import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import {
  CreateUserRequest,
  ProvisionTenantRequest,
  ProvisionTenantResponse,
  TenantDto,
  UpdateTenantRequest,
  UpdateUserRequest,
  UserDto,
  SetUserPasswordRequest,
  ReplaceBusinessAdminRequest,
  ReplaceBusinessAdminResponse,
  LocationDto,
  UserLocationsDto,
} from '../models/user-admin.models';

@Injectable({ providedIn: 'root' })
export class UserAdminApi {
  private readonly http = inject(HttpClient);
  private readonly authApiBaseUrl = '/api/auth/api';
  private readonly bhpApiBaseUrl = '/api/bhp/api';

  listUsers(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(`${this.authApiBaseUrl}/users/`);
  }

  getUser(userCode: string): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.authApiBaseUrl}/user/${userCode}/`);
  }

  listTenants(): Observable<TenantDto[]> {
    return this.http.get<TenantDto[]>(`${this.authApiBaseUrl}/tenants/`);
  }

  createUser(payload: CreateUserRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.authApiBaseUrl}/user/`, payload);
  }

  updateUser(userCode: string, payload: Partial<UpdateUserRequest>): Observable<UserDto> {
    return this.http.patch<UserDto>(`${this.authApiBaseUrl}/user/${userCode}/`, payload);
  }

  setUserPassword(userCode: string, payload: SetUserPasswordRequest): Observable<{ must_change_password: boolean }> {
    return this.http.post<{ must_change_password: boolean }>(`${this.authApiBaseUrl}/user/${userCode}/password/`, payload);
  }

  provisionTenant(payload: ProvisionTenantRequest): Observable<ProvisionTenantResponse> {
    return this.http.post<ProvisionTenantResponse>(
      `${this.authApiBaseUrl}/provision-tenant/`,
      payload,
    );
  }

  updateTenant(tenantId: string, payload: UpdateTenantRequest): Observable<TenantDto> {
    return this.http.patch<TenantDto>(`${this.authApiBaseUrl}/tenant/${tenantId}/`, payload);
  }

  replaceBusinessAdmin(
    tenantId: string,
    payload: ReplaceBusinessAdminRequest,
  ): Observable<ReplaceBusinessAdminResponse> {
    return this.http.post<ReplaceBusinessAdminResponse>(
      `${this.authApiBaseUrl}/tenant/${tenantId}/replace-business-admin/`,
      payload,
    );
  }

  provisionBhpTenant(
    name: string,
    schema: string,
  ): Observable<{ name: string; schema: string; created: boolean }> {
    return this.http.post<{ name: string; schema: string; created: boolean }>(
      `${this.bhpApiBaseUrl}/provision-tenant/`,
      { name, schema },
    );
  }

  listLocations(): Observable<{ results: LocationDto[] }> {
    return this.http.get<{ results: LocationDto[] }>(`${this.bhpApiBaseUrl}/locations/`);
  }

  getUserLocations(userCode: string): Observable<UserLocationsDto> {
    return this.http.get<UserLocationsDto>(`${this.bhpApiBaseUrl}/userLocations/${userCode}/`);
  }

  assignUserLocations(userCode: string, locations: string[]): Observable<UserLocationsDto> {
    return this.http.post<UserLocationsDto>(`${this.bhpApiBaseUrl}/userLocations/`, {
      user_code: userCode,
      locations,
    });
  }

  createLocation(name: string): Observable<LocationDto> {
    return this.http.post<LocationDto>(`${this.bhpApiBaseUrl}/locations/`, {
      name,
      status: 'active',
    });
  }
}
